import { useLocations } from 'apps/wrapper/src/hooks/use-locations';
import { useProductsByLocation } from 'apps/wrapper/src/hooks/use-products-by-location';
import { FC, useMemo } from 'react';
import { useHistory } from '../../../hooks/use-history-intercept';
import { useShowPaymentEnrollment } from '../../../hooks/use-show-payment-enrollment';
import { useWrapperNavigation } from '../../../hooks/use-wrapper-navigation';
import Sidebar from '../../shared/sidebar/sidebar';
import SidebarItem from '../../shared/sidebar/sidebar-item';
import SidebarItemWithSubMenu from '../../shared/sidebar/sidebar-item-with-sub-menu';
import { SubMenuType } from '../../shared/sidebar/sidebar-sub-menu';

import { useFeatureFlags } from 'apps/wrapper/src/hooks/feature-flags/use-feature-flags';
import { ReactComponent as CampaignsButtons } from '../../../assets/menu/icon-campaign.svg';
import { ReactComponent as EvaluateMyPracticeButton } from '../../../assets/menu/icon-evaluate-practice.svg';
import { ReactComponent as CoreButton } from '../../../assets/menu/icon-home.svg';
import { ReactComponent as MsgButton } from '../../../assets/menu/icon-messenger.svg';
import { ReactComponent as PatientsButton } from '../../../assets/menu/icon-patients.svg';
import { ReactComponent as ReputationButton } from '../../../assets/menu/icon-thumb_up.svg';
import { ReactComponent as PhoneButton } from '../../../assets/menu/icon-voip.svg';
import { ReactComponent as PaymentsButton } from '../../../assets/menu/icon-payments.svg';
import { ReactComponent as VirtualVisitsButton } from '../../../assets/menu/icon-vv.svg';
import { RoutePaths, PageRoutes } from '../../../routing/page-routes';
import FormsButton from './forms-button';
import InsightsButton from './insights-button';
import KayNButton from './kayn-button';
import MessengerButton from './messenger-button';
import SchedulerButton from './scheduler-button';
import TeamChatButton from './teamchat-button';

export interface MenuProps {
  onClick?: () => void;
}

const Menu: FC<MenuProps> = ({ onClick }) => {
  const history = useHistory();
  const { windowUrls } = useWrapperNavigation();
  const { selectedLocation } = useLocations();
  const locationId = selectedLocation?.id;
  const { hasFeature: checkFeature } = useFeatureFlags();
  const { hasProduct } = useProductsByLocation(locationId);
  const showPaymentEnrollment = useShowPaymentEnrollment(locationId);
  const hasFeature = (featureName: string) => checkFeature(locationId || 0, featureName);
  const hasMessenger2 = hasProduct('messenger2') && hasFeature('Messenger2');
  const hasPhone2 = hasProduct('phone2') && hasFeature('Phone2');
  const hasPhone = (hasProduct('phone') || hasProduct('phone1')) && (hasFeature('Phone') || hasFeature('Phone1'));
  const hasPatient2 = hasProduct('patients2') && hasFeature('Patients2');
  const hasPatient1 = hasProduct('patients1');
  const hasScheduler = hasProduct('scheduler') && hasFeature('OnlineScheduling');
  const hasCore = hasProduct('core');
  const hasReminders = hasProduct('reminders') && hasFeature('Reminders');
  const hasForms = hasProduct('forms') && hasFeature('Forms');
  const hasReputationMamangement = hasProduct('reputationmanagement') && hasFeature('Reputation');
  const hasCampaigns2 = hasProduct('campaign') && hasFeature('Campaign');
  const hasForms2 = hasProduct('forms2') && hasFeature('Forms2');
  const hasVirtualVisits = hasProduct('virtualvisits') && hasFeature('VirtualVisits');
  const hasEvaluateMyPractice = hasProduct('evaluatemypractice') && hasFeature('EvaluateMyPractice');
  const hasTeamChat = hasProduct('teamchat') && hasFeature('TeamChat');
  const hasInsights = hasProduct('insights') && hasFeature('Insights');
  const hasWelcomePackets = hasProduct('welcomepackets') && hasFeature('WelcomePackets');
  const hasPayments = hasProduct('payments') && hasFeature('Payments');
  const hasKayN = hasFeature('IVKayN');

  const activeFrame = useMemo(() => {
    const pathParts = history.location.pathname.split('/');

    if (pathParts.length > 1) return pathParts[1];

    return '';
  }, [history.location]);

  return (
    <Sidebar>
      {(hasCore || hasReminders || hasForms || hasCampaigns2 || hasForms2 || hasWelcomePackets || hasPayments) && (
        <SidebarItem title="Core" onClick={onClick} to={windowUrls.core} isActive={activeFrame === 'core'}>
          <CoreButton />
        </SidebarItem>
      )}
      {hasScheduler && (
        <SchedulerButton onClick={onClick} to={windowUrls.scheduler} isActive={activeFrame === 'scheduler'} />
      )}
      {hasPatient2 && (
        <SidebarItem title="Patients" onClick={onClick} to={windowUrls.contacts} isActive={activeFrame === 'contacts'}>
          <PatientsButton />
        </SidebarItem>
      )}
      {hasPatient1 && (
        <SidebarItem
          title="Patients"
          onClick={onClick}
          to={windowUrls.contacts1}
          isActive={activeFrame === 'contacts1'}
        >
          <PatientsButton />
        </SidebarItem>
      )}
      {hasMessenger2 ? (
        <MessengerButton to={windowUrls.messenger} onClick={onClick} isActive={activeFrame === 'messenger'} />
      ) : (
        <SidebarItem
          title="Messenger"
          onClick={onClick}
          to={windowUrls.messenger1}
          isActive={activeFrame === 'messenger1'}
        >
          <MsgButton />
        </SidebarItem>
      )}
      {hasPhone2 && (
        <SidebarItem title="Phone" onClick={onClick} to={windowUrls.phone} isActive={activeFrame === 'phone'}>
          <PhoneButton />
        </SidebarItem>
      )}
      {hasPhone && (
        <SidebarItem title="Phone" onClick={onClick} to={windowUrls.phone1} isActive={activeFrame === 'phone1'}>
          <PhoneButton />
        </SidebarItem>
      )}
      {hasReputationMamangement && (
        <SidebarItem
          title="Reputation"
          onClick={onClick}
          to={windowUrls.reputation}
          isActive={activeFrame === 'reputation'}
        >
          <ReputationButton />
        </SidebarItem>
      )}
      {hasKayN && <KayNButton to={'/insurance/dashboard'} isActive={activeFrame === 'insurance'} onClick={onClick} />}
      {hasInsights && (
        <InsightsButton onClick={onClick} to={windowUrls.insights} isActive={activeFrame === 'insights'} />
      )}
      {hasCampaigns2 && (
        <SidebarItem
          title="Campaigns"
          onClick={onClick}
          to={windowUrls.campaigns}
          isActive={activeFrame === 'campaigns'}
        >
          <CampaignsButtons className="CampainButton" />
        </SidebarItem>
      )}
      {hasForms2 && <FormsButton to={windowUrls.forms} isActive={activeFrame === 'forms'} onClick={onClick} />}
      {showPaymentEnrollment !== undefined && (
        <SidebarItem
          title="Payments"
          onClick={onClick}
          to={
            showPaymentEnrollment
              ? RoutePaths[PageRoutes.PAYMENTS_GET_STARTED]
              : RoutePaths[PageRoutes.PAYMENTS]
          }
          isActive={activeFrame === 'payments'}
        >
          <PaymentsButton />
        </SidebarItem>
      )}
      {hasVirtualVisits && (
        <SidebarItem
          title="Virtual Visits"
          onClick={onClick}
          to={windowUrls.virtualvisits}
          isActive={activeFrame === 'virtualvisits'}
        >
          <VirtualVisitsButton />
        </SidebarItem>
      )}
      {hasEvaluateMyPractice && locationId && (
        <SidebarItem
          title="Evaluate My Practice"
          onClick={onClick}
          to={`${windowUrls.evaluatemypractice}/?locationId=${locationId}`}
          isActive={activeFrame === 'evaluatemypractice'}
        >
          <EvaluateMyPracticeButton />
        </SidebarItem>
      )}
      {hasTeamChat && (
        <TeamChatButton to={windowUrls.teamchat} onClick={onClick} isActive={activeFrame === 'teamchat'} />
      )}
      <SidebarItemWithSubMenu
        title="My Secure Practice"
        menuType={SubMenuType.MY_SECURE_PRACTICE}
        activeFrame={activeFrame}
        locationId={locationId}
      />
      <SidebarItemWithSubMenu
        title="Digital Marketing"
        menuType={SubMenuType.PBHS_WEBSITE_ADMIN}
        activeFrame={activeFrame}
        locationId={locationId}
      />
    </Sidebar>
  );
};

export default Menu;
